import React from "react";
import SeoHead from "../seo/SeoHead";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/TransmissionFlush.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const TransmissionRepair = () => {
  return (
    <>
      <SeoHead
        title="Transmission Repair & Service | Temecula Auto Shop"
        description="Transmission problems? Slipping, hard shifts, or leaks—we diagnose and repair transmissions and related drivetrain components in Temecula. Call Advanced Automotive Smog."
        path="/transmission-repair"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="Transmission Repair"
          path="/transmission-repair"
          description="Transmission diagnosis and repair in Temecula for slipping, hard shifts, leaks, and related drivetrain components."
          serviceType="Transmission repair"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="Transmission service and repair"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>Transmission · Temecula</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Transmission Repair & Service
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              Your transmission transfers power to the wheels. When fluid is
              degraded, leaks appear, or internal wear sets in, shifts suffer
              and damage can spread. We evaluate symptoms, fluid condition, and
              codes to recommend the right service or repair.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs You May Need This Service
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Warning lights: transmission, check engine, or temperature</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Strange noises: whining, clunking on shifts, or grinding</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Performance issues: slipping, delayed engagement, or harsh shifts</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Red or brown fluid spots under the vehicle</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Services Included
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Road-test and computer scan for transmission codes</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                Fluid inspection and{" "}
                <Link to="/services/transmission-flush" className="text-teal-600 hover:underline">
                  transmission flush
                </Link>{" "}
                when appropriate
              </span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Linkage, mount, and external seal checks</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Related drivetrain: transfer case and differential coordination</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Repairs scoped after diagnosis—no unnecessary work</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Choose Us
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            We treat transmission concerns with systematic testing rather than
            guesswork. You will understand what we found, what it means for
            safety and longevity, and what we recommend before any major work
            begins.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Schedule transmission diagnosis
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default TransmissionRepair;
